import React, { useEffect, useState, useMemo } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography, CircularProgress, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../../services/api';
import { STUDENT_TOPIC_PERFORMANCE } from '../../config/Config';

const ResultsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topicFilter, setTopicFilter] = useState('');
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = topicFilter ? { topic: topicFilter } : {};
        const res = await api.get(STUDENT_TOPIC_PERFORMANCE, { params });
        const rows = Array.isArray(res.data) ? res.data : [];
        setData(rows);
        // derive list of topics from response
        const topicList = [];
        rows.forEach(r => {
          if (!topicList.find(t => t.id === r.topic_id)) {
            topicList.push({ id: r.topic_id, name: r.topic_name });
          }
        });
        setTopics(topicList);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [topicFilter]);

  // transform to recharts dataset: one object per attempt { name:'Attempt 1', Algebra:75, Geometry:60 }
  const chartData = useMemo(() => {
    const map = new Map();
    data.forEach(r => {
      const date = r.date ? new Date(r.date) : null;
      const dateStr = date ? date.toLocaleDateString(undefined, { day:'numeric', month:'short' }) : '';
      const key = `Attempt ${r.attempt}${dateStr?` - ${dateStr}`:''}`;
      if (!map.has(key)) map.set(key, { name: key });
      map.get(key)[r.topic_name] = r.percent;
    });
    return Array.from(map.values()).map(obj=>{
      // derive quarter label
      const attemptNumMatch = obj.name.match(/Attempt (\d+)/);
      const attemptIdx = attemptNumMatch ? Number(attemptNumMatch[1])-1 : 0;
      const src = data[attemptIdx];
      if(src && src.date){
        const d=new Date(src.date);
        const year=d.getFullYear();
        const quarter=Math.floor(d.getMonth()/3);
        const quarterNames=['Jan-Mar','Apr-Jun','Jul-Sep','Oct-Dec'];
        obj.quarter=`${quarterNames[quarter]},${year}`;
      }
      return obj;
    });
  }, [data]);

  const lineKeys = useMemo(() => {
    const set = new Set();
    data.forEach(r => set.add(r.topic_name));
    return Array.from(set);
  }, [data]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, overflowX: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton onClick={() => navigate(-1)} size="small" sx={{ mr: 1 }} aria-label="Go back"><ArrowBackIcon /></IconButton>
        <Typography variant="h5" gutterBottom sx={{ m: 0, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Results</Typography>
      </Box>
      <FormControl sx={{ width: '100%', minWidth: { xs: 0, sm: 200 }, mb: 2 }} size="small">
        <InputLabel>Topic</InputLabel>
        <Select
          value={topicFilter}
          label="Topic"
          onChange={e => setTopicFilter(e.target.value)}
        >
          <MenuItem value=""><em>All</em></MenuItem>
          {topics.map(t => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {loading ? (
        <CircularProgress />
      ) : (
        <ResponsiveContainer width="100%" height={400} minHeight={300}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{fontSize:12}} height={50} />
            {/* Second axis for quarter labels */}
            <XAxis
              dataKey="quarter"
              xAxisId="quarter"
              axisLine={false}
              tickLine={false}
              tick={{fontSize:12, dy:10}}
              interval={0}
              height={30}
            />
            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
            <Tooltip formatter={(v)=>`${v}%`} />
            <Legend />
            {lineKeys.map((k, idx) => (
              <Line key={k} type="monotone" dataKey={k} stroke={COLORS[idx % COLORS.length]} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

const COLORS = ['#8884d8','#82ca9d','#ff7300','#ff0000','#00bcd4','#8bc34a'];

export default ResultsPage;
